export const THEME_MODE_IDS = Object.freeze(['default', 'dark', 'pretty']);

const BASE_THEME_TOKEN_KEYS = Object.freeze([
  'bg',
  'surface0',
  'surface1',
  'surface2',
  'surface3',
  'border',
  'text',
  'textSubtle',
  'primary',
  'primaryHover',
  'primaryActive',
  'primaryText',
  'danger',
  'dangerHover',
  'dangerActive',
  'dangerText',
  'controlSurface',
  'controlBorder',
  'controlText',
  'controlPlaceholder',
  'pillDefaultBg',
  'pillDefaultBorder',
  'pillDefaultText',
  'pillPositiveBg',
  'pillPositiveBorder',
  'pillPositiveText',
  'pillWarningBg',
  'pillWarningBorder',
  'pillWarningText',
  'pillCurrentBg',
  'pillCurrentBorder',
  'pillCurrentText',
  'tableHeaderBg',
  'tableHeaderText',
  'tableRowBorder',
  'modalSurface',
  'modalBorder',
  'modalText',
  'shadowCard',
  'shadowControlIdle',
  'shadowControlHover',
  'shadowControlActive',
  'transitionFast',
  'transitionDefault',
  'focusRing',
  'surface',
  'highlight',
  'innerHighlight',
  'text2',
  'muted',
  'accent',
  'accentSoft',
  'warning',
  'success',
  'focus',
  'shadow1',
  'shadow2',
  'shadow3',
  'glowSoft',
  'overlayScrim',
  'overlaySurface',
  'overlayBorder',
  'overlayShadow',
  'overlayText',
  'overlayText2',
  'overlayFocus',
  'easeStandard',
  'durFast',
  'durMed'
]);

const SEMANTIC_TONE_IDS = Object.freeze(['neutral', 'brand', 'success', 'warning', 'danger', 'info']);
const ACCENT_HUE_IDS = Object.freeze(['01', '02', '03', '04', '05', '06', '07', '08']);
const ACCENT_STEP_IDS = Object.freeze(['01', '02', '03', '04', '05', '06']);

export const THEME_TOKEN_KEYS = Object.freeze([
  ...BASE_THEME_TOKEN_KEYS,
  ...buildToneTokenKeys(),
  ...buildAccentPaletteTokenKeys()
]);

const DEFAULT_THEME_MODE_ID = 'default';

const DEFAULT_THEME_TOKENS = Object.freeze(withColorContractTokens({
  bg: '#f5f8fd',
  surface0: 'rgba(255, 255, 255, 0.78)',
  surface1: '#ffffff',
  surface2: '#f8fbff',
  surface3: '#eef4ff',
  border: 'rgba(148, 163, 184, 0.28)',
  text: '#0f172a',
  textSubtle: '#475569',
  primary: '#2563eb',
  primaryHover: '#1d4ed8',
  primaryActive: '#1e40af',
  primaryText: '#f8fafc',
  danger: '#dc2626',
  dangerHover: '#b91c1c',
  dangerActive: '#991b1b',
  dangerText: '#fff7f7',
  controlSurface: '#ffffff',
  controlBorder: 'rgba(148, 163, 184, 0.52)',
  controlText: '#0f172a',
  controlPlaceholder: '#6b7280',
  pillDefaultBg: 'rgba(241, 245, 249, 0.96)',
  pillDefaultBorder: 'rgba(148, 163, 184, 0.34)',
  pillDefaultText: '#334155',
  pillPositiveBg: 'rgba(220, 252, 231, 0.96)',
  pillPositiveBorder: 'rgba(34, 197, 94, 0.34)',
  pillPositiveText: '#166534',
  pillWarningBg: 'rgba(255, 251, 235, 0.96)',
  pillWarningBorder: 'rgba(245, 158, 11, 0.38)',
  pillWarningText: '#92400e',
  pillCurrentBg: 'rgba(224, 231, 255, 0.96)',
  pillCurrentBorder: 'rgba(79, 70, 229, 0.28)',
  pillCurrentText: '#4338ca',
  tableHeaderBg: 'linear-gradient(90deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.96))',
  tableHeaderText: '#e2e8f0',
  tableRowBorder: 'rgba(148, 163, 184, 0.16)',
  modalSurface: 'rgba(255, 255, 255, 0.96)',
  modalBorder: 'rgba(148, 163, 184, 0.18)',
  modalText: '#0f172a',
  shadowCard: '0 18px 34px rgba(15, 23, 42, 0.08)',
  shadowControlIdle: '0 8px 22px rgba(15, 23, 42, 0.05)',
  shadowControlHover: '0 14px 28px rgba(15, 23, 42, 0.08)',
  shadowControlActive: '0 9px 18px rgba(15, 23, 42, 0.11)',
  transitionFast: '120ms ease',
  transitionDefault: '180ms ease',
  focusRing: '0 0 0 3px rgba(37, 99, 235, 0.24)',
  surface: '#ffffff',
  highlight: 'rgba(255, 255, 255, 0.72)',
  innerHighlight: 'rgba(255, 255, 255, 0.72)',
  text2: '#1e293b',
  muted: '#64748b',
  accent: '#2563eb',
  accentSoft: 'rgba(219, 234, 254, 0.72)',
  warning: '#d97706',
  success: '#059669',
  focus: 'rgba(37, 99, 235, 0.24)',
  shadow1: '0 18px 34px rgba(15, 23, 42, 0.08)',
  shadow2: '0 22px 38px rgba(15, 23, 42, 0.12)',
  shadow3: '0 28px 48px rgba(15, 23, 42, 0.16)',
  glowSoft: '0 0 0 1px rgba(37, 99, 235, 0.06), 0 10px 26px rgba(37, 99, 235, 0.08)',
  overlayScrim: 'rgba(15, 23, 42, 0.24)',
  overlaySurface: 'linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(248, 251, 255, 0.98))',
  overlayBorder: 'rgba(148, 163, 184, 0.18)',
  overlayShadow: '0 24px 48px rgba(15, 23, 42, 0.14)',
  overlayText: '#0f172a',
  overlayText2: '#475569',
  overlayFocus: 'rgba(37, 99, 235, 0.22)',
  easeStandard: 'cubic-bezier(0.22, 1, 0.36, 1)',
  durFast: '120ms',
  durMed: '180ms'
}));

const RAW_THEME_MODE_DEFINITIONS = [
  {
    id: 'default',
    label: 'Default',
    description: 'Canonical baseline for the refined Settings UI.',
    version: 1,
    tokens: DEFAULT_THEME_TOKENS,
    preview: {
      surface: '#ffffff',
      accent: '#2563eb',
      secondary: '#0f172a'
    }
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'A dark-mode variant with the same structure and spacing.',
    version: 1,
    tokens: {
      bg: 'linear-gradient(180deg, #0b1220 0%, #111827 100%)',
      surface0: 'linear-gradient(180deg, rgba(11, 18, 32, 0.92), rgba(12, 20, 35, 0.96))',
      surface1: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98))',
      surface2: 'linear-gradient(180deg, rgba(19, 30, 48, 0.98), rgba(15, 23, 42, 0.98))',
      surface3: 'linear-gradient(180deg, rgba(27, 41, 64, 0.98), rgba(19, 30, 48, 0.98))',
      border: 'rgba(148, 163, 184, 0.18)',
      text: '#edf3ff',
      textSubtle: '#b6c2d4',
      primary: '#3b82f6',
      primaryHover: '#2563eb',
      primaryActive: '#1d4ed8',
      primaryText: '#eff6ff',
      danger: '#ef4444',
      dangerHover: '#dc2626',
      dangerActive: '#b91c1c',
      dangerText: '#fff5f5',
      controlSurface: '#0f1a2d',
      controlBorder: 'rgba(148, 163, 184, 0.24)',
      controlText: '#edf3ff',
      controlPlaceholder: '#8aa0bd',
      pillDefaultBg: 'rgba(30, 41, 59, 0.78)',
      pillDefaultBorder: 'rgba(148, 163, 184, 0.18)',
      pillDefaultText: '#d7e1ef',
      pillPositiveBg: 'rgba(6, 78, 59, 0.4)',
      pillPositiveBorder: 'rgba(16, 185, 129, 0.26)',
      pillPositiveText: '#b7f7d7',
      pillWarningBg: 'rgba(120, 53, 15, 0.34)',
      pillWarningBorder: 'rgba(251, 191, 36, 0.24)',
      pillWarningText: '#fde7b2',
      pillCurrentBg: 'rgba(37, 99, 235, 0.28)',
      pillCurrentBorder: 'rgba(96, 165, 250, 0.28)',
      pillCurrentText: '#dbeafe',
      tableHeaderBg: 'linear-gradient(90deg, rgba(21, 30, 48, 0.98), rgba(30, 41, 59, 0.98))',
      tableHeaderText: '#e8efff',
      tableRowBorder: 'rgba(148, 163, 184, 0.1)',
      modalSurface: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98))',
      modalBorder: 'rgba(148, 163, 184, 0.16)',
      modalText: '#edf3ff',
      shadowCard: '0 16px 34px rgba(2, 6, 23, 0.34)',
      shadowControlIdle: '0 8px 18px rgba(2, 6, 23, 0.2)',
      shadowControlHover: '0 12px 24px rgba(2, 6, 23, 0.26)',
      shadowControlActive: '0 6px 14px rgba(2, 6, 23, 0.28)',
      transitionFast: '120ms ease',
      transitionDefault: '180ms ease',
      focusRing: '0 0 0 3px rgba(96, 165, 250, 0.34)',
      surface: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98))',
      highlight: 'rgba(255, 255, 255, 0.05)',
      innerHighlight: 'rgba(255, 255, 255, 0.05)',
      text2: '#d9e6fb',
      muted: '#9aaec7',
      accent: '#3b82f6',
      accentSoft: 'rgba(37, 99, 235, 0.18)',
      warning: '#f59e0b',
      success: '#10b981',
      focus: 'rgba(96, 165, 250, 0.34)',
      shadow1: '0 16px 34px rgba(2, 6, 23, 0.34)',
      shadow2: '0 20px 38px rgba(2, 6, 23, 0.42)',
      shadow3: '0 28px 50px rgba(2, 6, 23, 0.48)',
      glowSoft: '0 0 0 1px rgba(59, 130, 246, 0.12), 0 12px 30px rgba(37, 99, 235, 0.18)',
      overlayScrim: 'rgba(2, 6, 23, 0.82)',
      overlaySurface: 'linear-gradient(180deg, rgba(15, 23, 42, 0.985), rgba(17, 24, 39, 0.975))',
      overlayBorder: 'rgba(148, 163, 184, 0.18)',
      overlayShadow: '0 28px 64px rgba(2, 6, 23, 0.56), 0 8px 24px rgba(2, 6, 23, 0.32)',
      overlayText: '#edf3ff',
      overlayText2: '#c4d1e4',
      overlayFocus: 'rgba(96, 165, 250, 0.32)',
      easeStandard: 'cubic-bezier(0.22, 1, 0.36, 1)',
      durFast: '120ms',
      durMed: '180ms'
    },
    preview: {
      surface: '#111827',
      accent: '#3b82f6',
      secondary: '#edf3ff'
    }
  },
  {
    id: 'pretty',
    label: 'Pretty',
    description: 'A depth-enhanced variant that keeps layout and spacing unchanged.',
    version: 1,
    tokens: {
      bg: 'linear-gradient(180deg, #eef5ff 0%, #f7f4ff 48%, #fffaf8 100%)',
      surface0: 'linear-gradient(180deg, rgba(255, 255, 255, 0.76), rgba(245, 248, 255, 0.72))',
      surface1: 'linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 255, 0.978))',
      surface2: 'linear-gradient(180deg, rgba(255, 255, 255, 0.995), rgba(242, 246, 255, 0.985))',
      surface3: 'linear-gradient(180deg, rgba(239, 244, 255, 0.98), rgba(234, 239, 252, 0.965))',
      border: 'rgba(148, 163, 184, 0.16)',
      text: '#172036',
      textSubtle: '#54627f',
      primary: '#4f46e5',
      primaryHover: '#4338ca',
      primaryActive: '#3730a3',
      primaryText: '#f8faff',
      danger: '#dc2626',
      dangerHover: '#b91c1c',
      dangerActive: '#991b1b',
      dangerText: '#fff7f7',
      controlSurface: 'linear-gradient(180deg, rgba(255, 255, 255, 0.985), rgba(246, 248, 255, 0.98))',
      controlBorder: 'rgba(129, 140, 248, 0.18)',
      controlText: '#172036',
      controlPlaceholder: '#7a869c',
      pillDefaultBg: 'rgba(241, 245, 255, 0.9)',
      pillDefaultBorder: 'rgba(129, 140, 248, 0.16)',
      pillDefaultText: '#475569',
      pillPositiveBg: 'rgba(220, 252, 231, 0.92)',
      pillPositiveBorder: 'rgba(16, 185, 129, 0.2)',
      pillPositiveText: '#166534',
      pillWarningBg: 'rgba(255, 247, 237, 0.94)',
      pillWarningBorder: 'rgba(217, 119, 6, 0.18)',
      pillWarningText: '#9a3412',
      pillCurrentBg: 'rgba(238, 242, 255, 0.94)',
      pillCurrentBorder: 'rgba(99, 102, 241, 0.18)',
      pillCurrentText: '#4338ca',
      tableHeaderBg: 'linear-gradient(180deg, rgba(238, 243, 255, 0.985), rgba(231, 237, 251, 0.97))',
      tableHeaderText: '#334155',
      tableRowBorder: 'rgba(148, 163, 184, 0.12)',
      modalSurface: 'linear-gradient(180deg, rgba(255, 255, 255, 0.992), rgba(244, 247, 255, 0.982))',
      modalBorder: 'rgba(148, 163, 184, 0.14)',
      modalText: '#172036',
      shadowCard: '0 14px 28px rgba(99, 102, 241, 0.1), 0 3px 8px rgba(15, 23, 42, 0.06)',
      shadowControlIdle: '0 10px 22px rgba(99, 102, 241, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.78)',
      shadowControlHover: '0 16px 34px rgba(99, 102, 241, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.84)',
      shadowControlActive: '0 8px 16px rgba(99, 102, 241, 0.08), inset 0 2px 5px rgba(15, 23, 42, 0.08)',
      transitionFast: '140ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      transitionDefault: '220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      focusRing: '0 0 0 3px rgba(99, 102, 241, 0.2)',
      surface: 'linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 255, 0.978))',
      highlight: 'rgba(255, 255, 255, 0.86)',
      innerHighlight: 'rgba(255, 255, 255, 0.88)',
      text2: '#24324a',
      muted: '#74829a',
      accent: '#4f46e5',
      accentSoft: 'rgba(236, 241, 255, 0.96)',
      warning: '#d97706',
      success: '#059669',
      focus: 'rgba(99, 102, 241, 0.22)',
      shadow1: '0 14px 28px rgba(99, 102, 241, 0.1), 0 3px 8px rgba(15, 23, 42, 0.06)',
      shadow2: '0 18px 38px rgba(99, 102, 241, 0.14), 0 6px 14px rgba(15, 23, 42, 0.08)',
      shadow3: '0 28px 56px rgba(99, 102, 241, 0.16), 0 12px 24px rgba(15, 23, 42, 0.1)',
      glowSoft: '0 0 0 1px rgba(99, 102, 241, 0.08), 0 12px 28px rgba(99, 102, 241, 0.14)',
      overlayScrim: 'rgba(15, 23, 42, 0.24)',
      overlaySurface: '#f7f9ff',
      overlayBorder: 'rgba(148, 163, 184, 0.14)',
      overlayShadow: '0 28px 56px rgba(99, 102, 241, 0.16), 0 12px 24px rgba(15, 23, 42, 0.1)',
      overlayText: '#172036',
      overlayText2: '#5b6883',
      overlayFocus: 'rgba(99, 102, 241, 0.18)',
      easeStandard: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      durFast: '140ms',
      durMed: '220ms'
    },
    preview: {
      surface: '#f8f9ff',
      accent: '#4f46e5',
      secondary: '#c7d2fe'
    }
  }
];

function cloneThemeModeDefinition(definition) {
  if (!definition) return null;
  return {
    ...definition,
    tokens: { ...definition.tokens },
    preview: { ...definition.preview }
  };
}

function buildToneTokenKeys() {
  return SEMANTIC_TONE_IDS.flatMap((toneId) => ([
    `tone.${toneId}.bg`,
    `tone.${toneId}.border`,
    `tone.${toneId}.text`,
    `tone.${toneId}.hover`,
    `tone.${toneId}.active`,
    `tone.${toneId}.focus`
  ]));
}

function buildAccentPaletteTokenKeys() {
  return ACCENT_HUE_IDS.flatMap((hueId) => (
    ACCENT_STEP_IDS.map((stepId) => `accent.h${hueId}.s${stepId}`)
  ));
}

function withColorContractTokens(tokens = {}) {
  const source = tokens && typeof tokens === 'object' ? tokens : {};
  return {
    ...source,
    ...buildToneTokens(source),
    ...buildAccentTokens(source)
  };
}

function buildToneTokens(source) {
  return {
    'tone.neutral.bg': source.surface0 || source.surface1 || source.surface || '',
    'tone.neutral.border': source.border || source.controlBorder || '',
    'tone.neutral.text': source.text || source.controlText || '',
    'tone.neutral.hover': source.surface2 || source.surface1 || source.surface || '',
    'tone.neutral.active': source.surface3 || source.surface2 || source.surface1 || '',
    'tone.neutral.focus': source.focus || source.focusRing || '',
    'tone.brand.bg': source.primary || source.accent || '',
    'tone.brand.border': source.primaryHover || source.primary || '',
    'tone.brand.text': source.primaryText || source.text || '',
    'tone.brand.hover': source.primaryHover || source.primary || '',
    'tone.brand.active': source.primaryActive || source.primaryHover || source.primary || '',
    'tone.brand.focus': source.focus || source.focusRing || '',
    'tone.success.bg': source.success || source.pillPositiveBg || '',
    'tone.success.border': source.pillPositiveBorder || source.success || '',
    'tone.success.text': source.pillPositiveText || source.text || '',
    'tone.success.hover': source.pillPositiveBg || source.success || '',
    'tone.success.active': source.success || source.pillPositiveBg || '',
    'tone.success.focus': source.focus || source.focusRing || '',
    'tone.warning.bg': source.warning || source.pillWarningBg || '',
    'tone.warning.border': source.pillWarningBorder || source.warning || '',
    'tone.warning.text': source.pillWarningText || source.text || '',
    'tone.warning.hover': source.pillWarningBg || source.warning || '',
    'tone.warning.active': source.warning || source.pillWarningBg || '',
    'tone.warning.focus': source.focus || source.focusRing || '',
    'tone.danger.bg': source.danger || source.pillWarningBg || '',
    'tone.danger.border': source.dangerHover || source.danger || '',
    'tone.danger.text': source.dangerText || source.text || '',
    'tone.danger.hover': source.dangerHover || source.danger || '',
    'tone.danger.active': source.dangerActive || source.dangerHover || source.danger || '',
    'tone.danger.focus': source.focus || source.focusRing || '',
    'tone.info.bg': source.accentSoft || source.surface2 || source.surface1 || '',
    'tone.info.border': source.accent || source.primary || '',
    'tone.info.text': source.text || source.controlText || '',
    'tone.info.hover': source.highlight || source.accentSoft || source.surface2 || '',
    'tone.info.active': source.innerHighlight || source.highlight || source.surface3 || '',
    'tone.info.focus': source.focus || source.focusRing || ''
  };
}

function buildAccentTokens(source) {
  const paletteSteps = [
    source.accentSoft || source.highlight || source.surface2 || '',
    source.accent || source.primary || '',
    source.primary || source.accent || '',
    source.primaryHover || source.primary || source.accent || '',
    source.primaryActive || source.primaryHover || source.primary || '',
    source.focus || source.focusRing || source.accent || ''
  ];
  const hueBases = [
    source.accent || source.primary || '',
    source.primary || source.accent || '',
    source.success || source.pillPositiveBorder || source.accent || '',
    source.warning || source.pillWarningBorder || source.accent || '',
    source.danger || source.dangerHover || source.accent || '',
    source.textSubtle || source.muted || source.text || '',
    source.border || source.controlBorder || source.accentSoft || '',
    source.surface3 || source.surface2 || source.surface1 || ''
  ];
  const tokens = {};
  ACCENT_HUE_IDS.forEach((hueId, hueIndex) => {
    ACCENT_STEP_IDS.forEach((stepId, stepIndex) => {
      const tokenKey = `accent.h${hueId}.s${stepId}`;
      tokens[tokenKey] = hueBases[hueIndex] || paletteSteps[stepIndex] || source.accent || source.primary || '';
    });
  });
  return tokens;
}

function normalizeThemeModeId(value) {
  return String(value || '').trim().toLowerCase();
}

export function getDefaultThemeModeId() {
  return DEFAULT_THEME_MODE_ID;
}

export function isThemeModeId(id) {
  return THEME_MODE_IDS.includes(normalizeThemeModeId(id));
}

export function validateThemeTokens(tokens = {}) {
  const source = withColorContractTokens(tokens && typeof tokens === 'object' ? tokens : {});
  const normalized = {};
  THEME_TOKEN_KEYS.forEach((tokenKey) => {
    const value = source[tokenKey];
    normalized[tokenKey] = typeof value === 'string'
      ? value
      : DEFAULT_THEME_TOKENS[tokenKey];
  });
  return normalized;
}

export function normalizeThemeModeDefinition(definition = {}) {
  const normalizedId = isThemeModeId(definition.id) ? normalizeThemeModeId(definition.id) : getDefaultThemeModeId();
  const normalizedPreviewSource = definition.preview && typeof definition.preview === 'object'
    ? definition.preview
    : {};

  return {
    id: normalizedId,
    label: String(definition.label || normalizedId).trim() || normalizedId,
    description: String(definition.description || '').trim(),
    version: Number.isFinite(definition.version) ? Number(definition.version) : 1,
    tokens: validateThemeTokens(definition.tokens),
    preview: {
      surface: String(normalizedPreviewSource.surface || '').trim() || DEFAULT_THEME_TOKENS.surface1,
      accent: String(normalizedPreviewSource.accent || '').trim() || DEFAULT_THEME_TOKENS.primary,
      secondary: String(normalizedPreviewSource.secondary || '').trim() || DEFAULT_THEME_TOKENS.textSubtle
    }
  };
}

const NORMALIZED_THEME_MODE_DEFINITIONS = Object.freeze(
  RAW_THEME_MODE_DEFINITIONS.map((definition) => normalizeThemeModeDefinition(definition))
);

const THEME_MODE_DEFINITION_MAP = new Map(
  NORMALIZED_THEME_MODE_DEFINITIONS.map((definition) => [definition.id, Object.freeze(definition)])
);

export function listThemeModes() {
  return THEME_MODE_IDS
    .map((themeModeId) => cloneThemeModeDefinition(THEME_MODE_DEFINITION_MAP.get(themeModeId)))
    .filter(Boolean);
}

export function getThemeModeDefinition(id) {
  const themeModeId = isThemeModeId(id) ? normalizeThemeModeId(id) : getDefaultThemeModeId();
  return cloneThemeModeDefinition(THEME_MODE_DEFINITION_MAP.get(themeModeId));
}

function assertThemeRegistryContract() {
  if (THEME_MODE_IDS.join('|') !== 'default|dark|pretty') {
    throw new Error('THEME_MODE_IDS must remain [default, dark, pretty].');
  }
  if (getDefaultThemeModeId() !== 'default') {
    throw new Error('The default theme mode id must remain "default".');
  }
  const normalized = validateThemeTokens({
    bg: 42,
    unknown: '#fff'
  });
  if (Object.keys(normalized).length !== THEME_TOKEN_KEYS.length) {
    throw new Error('Theme token normalization must return the canonical schema only.');
  }
  if (normalized.bg !== DEFAULT_THEME_TOKENS.bg) {
    throw new Error('Non-string theme token values must fall back to default tokens.');
  }
}

assertThemeRegistryContract();
